import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { DecimalInput } from "@/components/DecimalInput";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Save, Trash2, Sparkles, Loader2 } from "lucide-react";

const BIG7 = [
  { k: "energy_kj", label: "Energie", unit: "kJ" },
  { k: "energy_kcal", label: "Energie", unit: "kcal" },
  { k: "fat", label: "Tuky", unit: "g" },
  { k: "saturated", label: "z toho nasycené mastné kys.", unit: "g" },
  { k: "carbs", label: "Sacharidy", unit: "g" },
  { k: "sugars", label: "z toho cukry", unit: "g" },
  { k: "protein", label: "Bílkoviny", unit: "g" },
  { k: "salt", label: "Sůl", unit: "g" },
];

const emptyForm = () => ({
  energy_kj: 0, energy_kcal: 0, fat: 0, saturated: 0,
  carbs: 0, sugars: 0, protein: 0, salt: 0, allergens: [],
});

const STATUS = {
  saved: { text: "Uloženo ručně", cls: "border-primary/40 text-primary bg-primary/5" },
  suggested: { text: "Návrh z databáze", cls: "border-secondary/40 text-secondary bg-secondary/5" },
  none: { text: "Bez dat", cls: "text-muted-foreground" },
};

export function NutritionEditor({ code }) {
  const [allergensList, setAllergensList] = useState([]);
  const [form, setForm] = useState(emptyForm());
  const [status, setStatus] = useState("none");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const r = await api.get(`/materials/${code}/nutrition`);
    const d = r.data;
    setStatus(d.status || "none");
    setForm({
      energy_kj: d.energy_kj || 0, energy_kcal: d.energy_kcal || 0,
      fat: d.fat || 0, saturated: d.saturated || 0, carbs: d.carbs || 0,
      sugars: d.sugars || 0, protein: d.protein || 0, salt: d.salt || 0,
      allergens: d.allergens || [],
    });
    setLoading(false);
  }, [code]);

  useEffect(() => { api.get("/allergens").then((r) => setAllergensList(r.data)); }, []);
  useEffect(() => { load(); }, [load]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleAllergen = (id) =>
    setForm((f) => ({
      ...f,
      allergens: f.allergens.includes(id)
        ? f.allergens.filter((a) => a !== id)
        : [...f.allergens, id].sort((a, b) => a - b),
    }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/materials/${code}/nutrition`, { ...form, source: "manual" });
      toast.success("Výživové hodnoty uloženy");
      await load();
    } catch {
      toast.error("Uložení se nezdařilo");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    setSaving(true);
    try {
      await api.delete(`/materials/${code}/nutrition`);
      toast.success("Uložené hodnoty smazány");
      await load();
    } catch {
      toast.error("Smazání se nezdařilo");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground py-8 justify-center">
        <Loader2 className="h-4 w-4 animate-spin" /> Načítám…
      </div>
    );
  }

  const st = STATUS[status] || STATUS.none;

  return (
    <div className="space-y-5" data-testid="nutrition-editor">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Badge variant="outline" className={st.cls} data-testid="nutrition-status">{st.text}</Badge>
          {status === "suggested" && (
            <span className="text-xs text-muted-foreground flex items-center gap-1">
              <Sparkles className="h-3 w-3 text-secondary" /> Předvyplněno z vestavěného číselníku — zkontrolujte a uložte.
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {status === "saved" && (
            <Button variant="ghost" size="sm" onClick={remove} disabled={saving}
              className="text-muted-foreground hover:text-destructive" data-testid="nutrition-delete">
              <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Smazat
            </Button>
          )}
          <Button size="sm" onClick={save} disabled={saving} className="rounded-full" data-testid="nutrition-save">
            <Save className="h-3.5 w-3.5 mr-1.5" /> Uložit
          </Button>
        </div>
      </div>

      <div>
        <Label className="text-xs uppercase tracking-[0.15em] font-bold text-muted-foreground">Výživové hodnoty na 100 g / 100 ml</Label>
        <div className="grid grid-cols-2 gap-3 mt-3">
          {BIG7.map((f) => (
            <div key={f.k} className="flex items-center gap-2">
              <span className="text-sm flex-1 text-muted-foreground">{f.label}</span>
              <DecimalInput data-testid={`nutrition-${f.k}`} value={form[f.k]}
                onValueChange={(n) => setField(f.k, n)} className="h-8 w-24 text-right num" />
              <span className="text-xs text-muted-foreground w-10">{f.unit}</span>
            </div>
          ))}
        </div>
      </div>

      <div>
        <Label className="text-xs uppercase tracking-[0.15em] font-bold text-muted-foreground">Alergeny (EU 14)</Label>
        <div className="grid grid-cols-2 gap-x-4 gap-y-2 mt-3">
          {allergensList.map((a) => (
            <label key={a.id} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`allergen-opt-${a.id}`}>
              <Checkbox checked={form.allergens.includes(a.id)} onCheckedChange={() => toggleAllergen(a.id)} />
              <span><span className="text-muted-foreground num">{a.id}.</span> {a.name}</span>
            </label>
          ))}
        </div>
      </div>
    </div>
  );
}
